import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { checkUsage, errorMessage, logoutAllSessions, logoutSession, readSession, requestOtp as apiRequestOtp, verifyOtp as apiVerifyOtp, writeSession } from "../lib/apiClient";
import { SessionInfo, Usage } from "../types";

interface AuthContextValue {
  session: SessionInfo | null;
  usage: Usage | null;
  isPro: boolean;
  canUsePeerComparison: boolean;
  canUseWhatIf: boolean;
  canUsePremiumPdf: boolean;
  canUseValuation: boolean;
  initialising: boolean;
  requestOtp: (email: string) => Promise<{ ok: boolean; message: string }>;
  verifyOtp: (email: string, otp: string) => Promise<{ ok: boolean; message: string }>;
  refreshUsage: () => Promise<void>;
  setUsage: (usage: Usage | null) => void;
  logout: () => void;
  logoutAllDevices: () => Promise<void>;
}

// See the matching note in ScanContext.tsx - exported for the dev-only
// design preview harness, not for app code.
export type { AuthContextValue };
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function fallbackFreeUsage(email: string): Usage {
  return {
    allowed: false,
    email,
    plan: "unavailable",
    subscription_status: "unavailable",
    used: 0,
    usage_count: 0,
    limit: 5,
    remaining: 0,
    free_scans_remaining: 0,
    syncing: true,
    sync_error: true,
  };
}

function normalizeUsage(usage: Usage | null): Usage | null {
  if (!usage) return usage;
  const remaining = usage.remaining;
  const limit = usage.limit;
  const used = usage.used ?? usage.usage_count ?? (
    typeof limit === "number" && typeof remaining === "number" ? Math.max(limit - remaining, 0) : 0
  );
  return {
    ...usage,
    used,
    usage_count: usage.usage_count ?? used,
  };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<SessionInfo | null>(() => readSession());
  const [usage, setUsage] = useState<Usage | null>(() => {
    const current = readSession();
    return current ? fallbackFreeUsage(current.email) : null;
  });
  const [initialising, setInitialising] = useState(true);

  const logout = useCallback(() => {
    const current = readSession();
    writeSession(null);
    setSession(null);
    setUsage(null);
    // Revoke the token server-side too, so it can't be replayed if it
    // leaked - best-effort, since the user should still be signed out
    // locally even if this call fails (network blip, expired token, etc).
    if (current) logoutSession(current.email, current.token).catch(() => {});
  }, []);

  // Unlike logout(), this is NOT best-effort: the whole point is killing
  // every other device's session too, so a failed call must not clear
  // local state - that would tell the user "you're signed out everywhere"
  // while other sessions are actually still live.
  const logoutAllDevices = useCallback(async () => {
    const current = readSession();
    if (!current) return;
    await logoutAllSessions(current.email, current.token);
    writeSession(null);
    setSession(null);
    setUsage(null);
  }, []);

  const refreshUsage = useCallback(async () => {
    const current = readSession();
    if (!current) return;
    try {
      const u = await checkUsage(current.email, current.token);
      setUsage(normalizeUsage(u));
    } catch (err: any) {
      if (err?.response?.status === 401) {
        logout();
        return;
      }
      // A failed refresh (network blip, brief backend redeploy, rate limit) is not
      // proof the account has no plan. Keep showing the last known-good plan/credits
      // and just flag that this refresh didn't succeed, instead of overwriting real
      // balances with a fake "Free / 0 scans" placeholder — a paying user should
      // never see their purchased credits vanish because of a transient error.
      setUsage((prev) => (prev ? { ...prev, sync_error: true } : fallbackFreeUsage(current.email)));
    }
  }, [logout]);

  useEffect(() => {
    (async () => {
      if (session) await refreshUsage();
      setInitialising(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestOtp = useCallback(async (email: string) => {
    try {
      const res = await apiRequestOtp(email.trim().toLowerCase());
      const devNote = (res as any)?.dev_note;
      return { ok: true, message: devNote ? `${res.message || "OTP sent."} (${devNote})` : res.message || "OTP sent to your email." };
    } catch (err) {
      return { ok: false, message: errorMessage(err, "Could not send the OTP. Please try again.") };
    }
  }, []);

  const verifyOtp = useCallback(async (email: string, otp: string) => {
    try {
      const res = await apiVerifyOtp(email.trim().toLowerCase(), otp.trim());
      if (!res.session_token) return { ok: false, message: "Verification response did not include a session token." };
      const newSession = { email: res.email, token: res.session_token };
      writeSession(newSession);
      setSession(newSession);
      if (res.usage) setUsage(normalizeUsage(res.usage));
      else {
        setUsage(fallbackFreeUsage(newSession.email));
        try {
          const u = await checkUsage(newSession.email, newSession.token);
          setUsage(normalizeUsage(u));
        } catch {
          setUsage(fallbackFreeUsage(newSession.email));
        }
      }
      return { ok: true, message: "Welcome to Tenet." };
    } catch (err) {
      return { ok: false, message: errorMessage(err, "Invalid or expired OTP.") };
    }
  }, []);

  const isPro = Boolean(usage?.admin || usage?.feature_access?.future_pro_features);
  const canUsePeerComparison = Boolean(usage?.admin || usage?.feature_access?.peer_comparison);
  const canUseWhatIf = Boolean(usage?.admin || usage?.feature_access?.what_if_simulation);
  const canUsePremiumPdf = Boolean(usage?.admin || usage?.feature_access?.premium_pdf);
  const canUseValuation = Boolean(usage?.admin || usage?.feature_access?.valuation_analysis);

  const setNormalizedUsage = useCallback((next: Usage | null) => {
    setUsage(normalizeUsage(next));
  }, []);

  const value = useMemo(
    () => ({ session, usage, isPro, canUsePeerComparison, canUseWhatIf, canUsePremiumPdf, canUseValuation, initialising, requestOtp, verifyOtp, refreshUsage, setUsage: setNormalizedUsage, logout, logoutAllDevices }),
    [session, usage, isPro, canUsePeerComparison, canUseWhatIf, canUsePremiumPdf, canUseValuation, initialising, requestOtp, verifyOtp, refreshUsage, setNormalizedUsage, logout, logoutAllDevices]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
